import { Accessor, createEffect, onCleanup } from "solid-js";

interface UseClickOutsideProps {
  // Function that returns the element to watch (the menu/container)
  ref: () => HTMLElement | undefined;
  // Action to execute when clicking outside
  callback: (event: Event) => void;
  // Optional: Condition to enable the listener (e.g., isOpen)
  enabled?: Accessor<boolean>;
  // Optional: Element to ignore (e.g., the button that opens the menu)
  ignoreRef?: () => HTMLElement | undefined;
  // Optional: Event type to listen for (default: "click")
  eventName?: "click" | "mousedown" | "mouseup" | "pointerdown" | "pointerup";
}

/**
 * Hook to detect clicks outside a specific element.
 * Useful for closing menus, modals, or dropdowns when the user clicks elsewhere.
 */
export function useClickOutside(props: UseClickOutsideProps) {
  createEffect(() => {
    // If enabled is not provided, assume true
    const isEnabled = props.enabled ? props.enabled() : true;

    if (isEnabled) {
      const handleClickOutside = (event: Event) => {
        const el = props.ref();
        const ignoreEl = props.ignoreRef?.();
        const target = event.target as Node;

        // If the element exists and the click is NOT inside it
        if (el && !el.contains(target)) {
          // If there is an ignore element and the click is inside it, do nothing
          if (ignoreEl && ignoreEl.contains(target)) {
            return;
          }
          props.callback(event);
        }
      };

      const eventName = props.eventName || "click";
      document.addEventListener(eventName, handleClickOutside);

      onCleanup(() => {
        document.removeEventListener(eventName, handleClickOutside);
      });
    }
  });
}
